import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, ShoppingCart, Info, Check, AlertTriangle, Sparkles, Heart, Shield, Zap } from 'lucide-react';
import { SauceData } from '../data/sauces';
import { FunctionalIngredient, functionalIngredientUtils } from '../data/functionalIngredients';

interface SaucePersonalizerProps {
  sauce: SauceData;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (sauceId: string, selectedIngredients: string[], totalPrice: number) => void;
  recommendedIngredients?: FunctionalIngredient[];
  quizProfile?: any;
}

export default function SaucePersonalizer({ 
  sauce, 
  isOpen, 
  onClose, 
  onAddToCart, 
  recommendedIngredients = [],
  quizProfile 
}: SaucePersonalizerProps) {
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<'beauty' | 'wellness' | 'performance'>('beauty');
  const [showInfo, setShowInfo] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedIngredients([]);
      setQuantity(1);
      setShowInfo(null);
      
      // Auto-select recommended ingredients if available
      if (recommendedIngredients.length > 0) {
        const recommendedIds = recommendedIngredients.slice(0, 3).map(ing => ing.id);
        setSelectedIngredients(recommendedIds);
      }
    }
  }, [isOpen, recommendedIngredients]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const allIngredients = functionalIngredientUtils.getAllIngredients();
  const categoryIngredients = functionalIngredientUtils.getIngredientsByCategory(activeCategory);
  
  const toggleIngredient = (ingredientId: string) => {
    setSelectedIngredients(prev => {
      if (prev.includes(ingredientId)) {
        return prev.filter(id => id !== ingredientId);
      } else if (prev.length < 5) {
        return [...prev, ingredientId];
      }
      return prev; // Don't add if already at max
    });
  };

  const getSelectedIngredientDetails = () => {
    return selectedIngredients.map(id => functionalIngredientUtils.getIngredientById(id)!).filter(Boolean);
  };

  const calculateTotalPrice = () => {
    const basePrice = sauce.price * quantity;
    const ingredientsPrice = functionalIngredientUtils.calculateTotalPrice(selectedIngredients) * quantity;
    return basePrice + ingredientsPrice;
  };

  const handleAddToCart = () => {
    const validation = functionalIngredientUtils.validateSelection(selectedIngredients);
    if (validation.valid) {
      onAddToCart(sauce.id, selectedIngredients, calculateTotalPrice());
      onClose();
    }
  };

  const getCategoryIcon = (category: 'beauty' | 'wellness' | 'performance') => {
    switch (category) {
      case 'beauty': return Heart;
      case 'wellness': return Shield;
      case 'performance': return Zap;
    }
  };

  return (
    <>
      <style jsx>{`
        .modal-backdrop {
          backdrop-filter: blur(8px);
          background: rgba(0, 0, 0, 0.8);
        }
        
        .modal-content {
          background: linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98));
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
        
        .ingredient-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }
        
        .ingredient-card:hover {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }
        
        .ingredient-card.selected {
          background: rgba(236, 72, 153, 0.2);
          border: 1px solid rgba(236, 72, 153, 0.5);
        }
        
        .category-tab {
          transition: all 0.3s ease;
        }
        
        .category-tab.active {
          background: linear-gradient(135deg, rgba(236, 72, 153, 0.3), rgba(139, 92, 246, 0.3));
          border: 1px solid rgba(236, 72, 153, 0.5);
        }
        
        .recommended-badge {
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        .ingredient-info {
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          z-index: 1000;
        }
        
        .scrollbar-thin {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
        }
        
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255, 255, 255, 0.5);
        }
        
        /* Mobile responsive improvements */
        @media (max-width: 1024px) {
          .modal-content {
            margin: 1rem;
            max-height: calc(100vh - 2rem);
          }
        }
        
        @media (max-width: 768px) {
          .modal-content {
            margin: 0.5rem;
            max-height: calc(100vh - 1rem);
          }
        }
      `}</style>

      {/* Modal Backdrop */}
      <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-2 sm:p-4">
        <div className="modal-content rounded-xl sm:rounded-2xl w-full max-w-7xl h-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header - Fixed */}
          <div className="flex-shrink-0 flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${sauce.color} rounded-xl flex items-center justify-center`}>
                <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-white">Personalize {sauce.name}</h2>
                <p className="text-gray-400 text-sm sm:text-base">Add functional ingredients to enhance your sauce</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            {/* Left Panel - Ingredient Selection */}
            <div className="flex-1 p-4 sm:p-6 overflow-y-auto scrollbar-thin">
              {/* Quiz-based Recommendations */}
              {recommendedIngredients.length > 0 && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl">
                  <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                    <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                    <h3 className="text-base sm:text-lg font-semibold text-white">Recommended for You</h3>
                  </div>
                  <p className="text-gray-300 text-xs sm:text-sm mb-2 sm:mb-3">
                    Based on your {quizProfile?.quizType === 'persona' ? 'wellness goals' : 'flavor profile'}
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:gap-3">
                    {recommendedIngredients.slice(0, 3).map((ingredient) => (
                      <div
                        key={ingredient.id}
                        className={`ingredient-card rounded-lg p-2 sm:p-3 cursor-pointer ${
                          selectedIngredients.includes(ingredient.id) ? 'selected' : ''
                        }`}
                        onClick={() => toggleIngredient(ingredient.id)}
                      >
                        <div className="flex items-center justify-between mb-1 sm:mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-base sm:text-lg">{ingredient.icon}</span>
                            <span className="text-white font-medium text-sm sm:text-base">{ingredient.name}</span>
                          </div>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <span className="recommended-badge text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-black font-bold">
                              ⭐
                            </span>
                            <span className="text-cyan-400 font-bold text-xs sm:text-sm">S${ingredient.price.toFixed(2)}</span>
                          </div>
                        </div>
                        <p className="text-gray-400 text-xs">{ingredient.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Tabs */}
              <div className="flex space-x-1 sm:space-x-2 mb-4 sm:mb-6 overflow-x-auto">
                {(['beauty', 'wellness', 'performance'] as const).map((category) => {
                  const Icon = getCategoryIcon(category);
                  return (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={`category-tab flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
                        activeCategory === category ? 'active text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="capitalize text-sm sm:text-base">{category}</span>
                      <span className="text-xs bg-white/20 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                        {functionalIngredientUtils.getIngredientsByCategory(category).length}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Selection Counter */}
              <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Selected Ingredients</span>
                  <span className={`font-bold text-sm sm:text-base ${selectedIngredients.length >= 5 ? 'text-red-400' : 'text-cyan-400'}`}>
                    {selectedIngredients.length}/5
                  </span>
                </div>
                {selectedIngredients.length >= 5 && (
                  <p className="text-red-400 text-xs mt-1">Maximum ingredients reached</p>
                )}
              </div>

              {/* Ingredients Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {categoryIngredients.map((ingredient) => {
                  const isSelected = selectedIngredients.includes(ingredient.id);
                  const isRecommended = recommendedIngredients.some(rec => rec.id === ingredient.id);
                  
                  return (
                    <div
                      key={ingredient.id}
                      className={`ingredient-card rounded-xl p-3 sm:p-4 cursor-pointer relative ${
                        isSelected ? 'selected' : ''
                      } ${selectedIngredients.length >= 5 && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => {
                        if (selectedIngredients.length < 5 || isSelected) {
                          toggleIngredient(ingredient.id);
                        }
                      }}
                    >
                      {isRecommended && (
                        <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 recommended-badge text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-black font-bold">
                          ⭐
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <span className="text-lg sm:text-2xl">{ingredient.icon}</span>
                          <div>
                            <h4 className="text-white font-semibold text-sm sm:text-base">{ingredient.name}</h4>
                            <p className="text-gray-400 text-xs">{ingredient.scientificName}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-cyan-400 font-bold text-sm sm:text-base">S${ingredient.price.toFixed(2)}</div>
                          {isSelected && (
                            <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-400 ml-auto mt-1" />
                          )}
                        </div>
                      </div>
                      
                      <p className="text-gray-300 text-xs sm:text-sm mb-2 sm:mb-3">{ingredient.description}</p>
                      
                      <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
                        {ingredient.benefits.slice(0, 2).map((benefit, index) => (
                          <span key={index} className="text-xs bg-white/10 text-gray-300 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                            {benefit}
                          </span>
                        ))}
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowInfo(showInfo === ingredient.id ? null : ingredient.id);
                        }}
                        className="text-cyan-400 hover:text-cyan-300 text-xs flex items-center space-x-1"
                      >
                        <Info className="h-3 w-3" />
                        <span>More info</span>
                      </button>
                      
                      {/* Ingredient Info Popup */}
                      {showInfo === ingredient.id && (
                        <div className="absolute top-full left-0 right-0 mt-2 ingredient-info rounded-lg p-3 sm:p-4 z-10">
                          <h5 className="text-white font-semibold mb-2 text-sm sm:text-base">Benefits:</h5>
                          <ul className="text-gray-300 text-xs space-y-1 mb-2 sm:mb-3">
                            {ingredient.benefits.map((benefit, index) => (
                              <li key={index}>• {benefit}</li>
                            ))}
                          </ul>
                          <div className="text-xs text-gray-400">
                            <p><strong>Dosage:</strong> {ingredient.dosage}</p>
                            {ingredient.contraindications && (
                              <p className="text-yellow-400 mt-1">
                                <strong>Note:</strong> {ingredient.contraindications.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Panel - Order Summary */}
            <div className="lg:w-80 xl:w-96 border-t lg:border-t-0 lg:border-l border-white/10 p-4 sm:p-6 bg-black/20 flex-shrink-0">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Order Summary</h3>
              
              {/* Base Sauce */}
              <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-white/5 rounded-lg">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r ${sauce.color} rounded-lg`}></div>
                  <div>
                    <h4 className="text-white font-medium text-sm sm:text-base">{sauce.name}</h4>
                    <p className="text-gray-400 text-xs sm:text-sm">{sauce.heat} heat level</p>
                  </div>
                </div>
                
                {/* Quantity Selector */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Quantity:</span>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-6 h-6 sm:w-8 sm:h-8 bg-white/10 rounded-lg flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                    >
                      <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                    <span className="text-white font-medium w-6 sm:w-8 text-center text-sm sm:text-base">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-6 h-6 sm:w-8 sm:h-8 bg-white/10 rounded-lg flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                    >
                      <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Selected Ingredients */}
              {selectedIngredients.length > 0 && (
                <div className="mb-3 sm:mb-4">
                  <h4 className="text-white font-medium mb-2 sm:mb-3 text-sm sm:text-base">Added Ingredients:</h4>
                  <div className="space-y-1.5 sm:space-y-2 max-h-32 sm:max-h-40 overflow-y-auto scrollbar-thin">
                    {getSelectedIngredientDetails().map((ingredient) => (
                      <div key={ingredient.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                        <div className="flex items-center space-x-1.5 sm:space-x-2">
                          <span className="text-xs sm:text-sm">{ingredient.icon}</span>
                          <span className="text-white text-xs sm:text-sm">{ingredient.name}</span>
                        </div>
                        <div className="flex items-center space-x-1.5 sm:space-x-2">
                          <span className="text-cyan-400 text-xs sm:text-sm">S${ingredient.price.toFixed(2)}</span>
                          <button
                            onClick={() => toggleIngredient(ingredient.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Breakdown */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-white/5 rounded-lg">
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between text-gray-300">
                    <span>Base sauce (×{quantity})</span>
                    <span>S${(sauce.price * quantity).toFixed(2)}</span>
                  </div>
                  {selectedIngredients.length > 0 && (
                    <div className="flex justify-between text-gray-300">
                      <span>Ingredients (×{quantity})</span>
                      <span>S${(functionalIngredientUtils.calculateTotalPrice(selectedIngredients) * quantity).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-1.5 sm:pt-2 flex justify-between text-white font-bold text-sm sm:text-base">
                    <span>Total</span>
                    <span>S${calculateTotalPrice().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-2.5 sm:py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>Add to Cart</span>
              </button>

              {/* Info Note */}
              <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start space-x-1.5 sm:space-x-2">
                  <Info className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-300">
                    <p className="font-medium mb-1">Personalization Note:</p>
                    <p>Functional ingredients are carefully measured and blended into your sauce for optimal taste and effectiveness.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}